import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { supabase, Imovel } from '../../lib/supabase';
import { useSEO } from '../../hooks/useSEO';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import Breadcrumb from '../../components/base/Breadcrumb';

export default function FavoritosPage() {
  const [imoveis, setImoveis] = useState<Imovel[]>([]);
  const [loading, setLoading] = useState(true);
  const [favoritos, setFavoritos] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);

  useEffect(() => {
    loadFavoritos();
  }, []);

  useEffect(() => {
    if (favoritos.length > 0) {
      loadImoveis();
    } else {
      setImoveis([]);
      setLoading(false);
    }
  }, [favoritos]);

  const loadFavoritos = () => {
    const saved = localStorage.getItem('favoritos');
    if (saved) {
      setFavoritos(JSON.parse(saved));
    } else {
      setLoading(false);
    }
  };

  const loadImoveis = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('imoveis')
        .select(`
          *,
          fotos:imovel_fotos(url)
        `)
        .in('id', favoritos)
        .eq('status', 'disponivel')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setImoveis(data || []);
    } catch (error) {
      console.error('Erro ao carregar imóveis favoritos:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorito = (imovelId: string) => {
    const newFavoritos = favoritos.filter(id => id !== imovelId);
    setFavoritos(newFavoritos);
    localStorage.setItem('favoritos', JSON.stringify(newFavoritos));
  };

  const handleNotificarPreco = async () => {
    if (!email) {
      alert('Por favor, digite seu e-mail');
      return;
    }

    if (!email.includes('@')) {
      alert('Por favor, digite um e-mail válido');
      return;
    }

    try {
      // Salvar e-mail no localStorage
      localStorage.setItem('notificacao_email', email);

      // Salvar favoritos no banco para notificações
      for (const imovelId of favoritos) {
        const imovel = imoveis.find(i => i.id === imovelId);
        if (imovel) {
          // Inserir ou atualizar favorito no banco
          await supabase
            .from('favoritos')
            .upsert({
              imovel_id: imovelId,
              email: email,
            }, {
              onConflict: 'imovel_id,email'
            });
        }
      }

      alert('✅ Pronto! Você receberá um e-mail quando houver redução de preço nos seus imóveis favoritos.');
      setShowEmailModal(false);
    } catch (error) {
      console.error('Erro ao salvar notificação:', error);
      alert('❌ Erro ao ativar notificações. Tente novamente.');
    }
  };

  // SEO
  useSEO({
    title: 'Meus Imóveis Favoritos | Mateus Oliveira',
    description: 'Veja todos os imóveis que você salvou como favoritos. Receba notificações quando houver redução de preço.',
    keywords: 'favoritos, imóveis salvos, notificação preço',
    canonical: window.location.origin + '/favoritos',
  });

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb items={[{ name: 'Meus Favoritos', url: '/favoritos' }]} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                  Meus <span className="text-orange-500">Favoritos</span>
                </h1>
                <p className="text-xl text-gray-600">
                  {favoritos.length === 0 
                    ? 'Você ainda não tem imóveis favoritos' 
                    : `${favoritos.length} ${favoritos.length === 1 ? 'imóvel salvo' : 'imóveis salvos'}`
                  }
                </p>
              </div>
              {favoritos.length > 0 && (
                <button
                  onClick={() => setShowEmailModal(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-lg transition-all whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-notification-line text-xl"></i>
                  Notificar Redução de Preço
                </button>
              )}
            </div>
          </motion.div>

          {/* Lista de Imóveis Favoritos */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
          ) : imoveis.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {imoveis.map((imovel, index) => (
                <motion.div
                  key={imovel.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                  <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 group">
                    <Link to={`/imovel/${imovel.slug}`} className="block">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={imovel.fotos?.[0]?.url || 'https://via.placeholder.com/400x300'}
                          alt={imovel.titulo}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        {imovel.destaque && (
                          <div className="absolute top-3 right-3 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                            Destaque
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-orange-500 uppercase">
                            {imovel.tipo}
                          </span>
                          <span className="text-xs text-gray-500">{imovel.codigo}</span>
                        </div>
                        <h3 className="text-base font-bold text-gray-800 mb-2 group-hover:text-orange-500 transition-colors line-clamp-2">
                          {imovel.titulo}
                        </h3>
                        <p className="text-gray-600 text-xs mb-3 flex items-center">
                          <i className="ri-map-pin-line mr-1"></i>
                          {imovel.bairro}, {imovel.cidade}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-3 pb-3 border-b">
                          <span className="flex items-center">
                            <i className="ri-hotel-bed-line mr-1"></i>
                            {imovel.quartos}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-shower-line mr-1"></i>
                            {imovel.banheiros}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-car-line mr-1"></i>
                            {imovel.vagas}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-ruler-line mr-1"></i>
                            {imovel.area_total}m²
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-bold text-orange-500">
                              {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              }).format(imovel.preco)}
                            </p>
                          </div>
                          <button className="bg-orange-500 text-white px-3 py-1.5 rounded-lg hover:bg-orange-600 transition-colors text-xs whitespace-nowrap">
                            Ver Detalhes
                          </button>
                        </div>
                      </div>
                    </Link>
                    {/* Botão de Remover Favorito */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        removeFavorito(imovel.id);
                      }}
                      className="absolute top-3 left-3 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all shadow-lg z-10"
                      title="Remover dos favoritos"
                    >
                      <i className="ri-heart-fill text-red-500 text-xl"></i>
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <div className="bg-white rounded-2xl shadow-lg p-12 max-w-2xl mx-auto">
                <i className="ri-heart-line text-6xl text-gray-300 mb-6"></i>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  Nenhum imóvel favorito ainda
                </h2>
                <p className="text-gray-600 mb-8">
                  Navegue pelos nossos imóveis e clique no ícone de coração para salvar seus favoritos
                </p>
                <Link
                  to="/imoveis"
                  className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-lg transition-all"
                >
                  <i className="ri-search-line mr-2"></i>
                  Buscar Imóveis
                </Link>
              </div>
            </motion.div>
          )}

          {/* Informações sobre Notificações */}
          {favoritos.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-12 bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 border border-orange-200"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-notification-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Receba Alertas de Redução de Preço
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Ative as notificações e receba um e-mail sempre que houver redução de preço nos imóveis que você salvou como favoritos.
                  </p>
                  <button
                    onClick={() => setShowEmailModal(true)}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-6 rounded-lg transition-all whitespace-nowrap"
                  >
                    Ativar Notificações
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Modal de E-mail */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-3xl text-orange-500"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Ativar Notificações
              </h2>
              <p className="text-gray-600">
                Digite seu e-mail para receber alertas de redução de preço
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Seu E-mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowEmailModal(false)}
                className="flex-1 px-6 py-3 text-gray-700 font-medium hover:bg-gray-100 rounded-xl transition-all whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleNotificarPreco}
                className="flex-1 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl transition-all whitespace-nowrap"
              >
                Ativar
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <WhatsAppButton />
      <Footer />
    </>
  );
}
